/**
 * Operational-period window math for the invoice re-issue capability.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the operational-period calendar
 * used by the re-issue window guard (spec IR-W1). It is pure, framework-free,
 * and environment-timezone-independent: every calendar computation is done on
 * date-only `YYYY-MM-DD` strings via UTC-calendar arithmetic (a pure
 * days-in-month table + integer month arithmetic). No `Date` or host timezone
 * is involved, so the result is identical in the backend and in the browser
 * regardless of the process timezone.
 *
 * Containing semantics (design D1, corrected): the "current" operational
 * period is the most-recently-started period — the one that CONTAINS the base
 * date. When `day(baseDate)` is strictly before the plain clamped start day of
 * the base month, the period start shifts back one month; on the boundary
 * (`day(baseDate) === startDay`) it does NOT shift. This matches spec scenario
 * 3 (`(15, 2026-09-05)` -> current.inicio `2026-08-15`) and the design D1
 * corrected expectations (which supersede the spec's internally-inconsistent
 * scenario 1/2 values).
 *
 * Watch item (c): this file also defines a string-based
 * `calculatePrepaidPeriods(baseDate, diaCobro, forwardPeriods, backwardPeriods)`.
 * The backend `apps/aid-backend/src/modules/billing/prepaid-periods.ts` keeps
 * its own DATE-based `calculatePrepaidPeriods(base, diaCobro, meses,
 * periodOffset)` under the same name as a behavior-neutral adapter over
 * `operationalPeriodStart` (design D2). The backend adapter MUST NOT
 * re-export this shared string-based `calculatePrepaidPeriods`; re-issue
 * consumers import `calculateReissueWindow` / `isInReissueWindow` from
 * `@aid/shared/cobros` instead.
 */

export interface ReissuePeriod {
  /** ISO date-only (`YYYY-MM-DD`) clamped start of the operational period. */
  inicio: string;
  /**
   * ISO date-only, HALF-OPEN end: `fin` of period N === `inicio` of period
   * N+1. (The accepted "half-open fin vs inclusive proration denominator"
   * inconsistency stays as-is — documented, not fixed.)
   */
  fin: string;
}

export interface ReissueWindow {
  previous: ReissuePeriod;
  current: ReissuePeriod;
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

const DAYS_31 = new Set([1, 3, 5, 7, 8, 10, 12]);

/** Pure calendar days-in-month (month is 1-based), including leap February. */
function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return isLeap ? 29 : 28;
  }
  return DAYS_31.has(month) ? 31 : 30;
}

/** Parse a strict `YYYY-MM-DD` value into its numeric components. */
function parseDateOnly(value: string): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) {
    throw new Error(`invalid date-only value '${value}'`);
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/**
 * The clamped start day of the operational period that begins in `baseDate`'s
 * month shifted by `monthOffset` months. This is the verbatim semantics of the
 * backend `startBillingPeriod` / `obtenerFechaMasCercana` (day-end clamp
 * `min(diaCobro, daysInMonth(year, month))`), ported to pure UTC-calendar
 * string arithmetic with no `Date`/TZ conversion.
 */
export function operationalPeriodStart(
  diaCobro: number,
  baseDate: string,
  monthOffset: number,
): string {
  const { year, month } = parseDateOnly(baseDate);
  const mesAbsoluto = month - 1 + monthOffset;
  const anio = year + Math.floor(mesAbsoluto / 12);
  const mes = (((mesAbsoluto % 12) + 12) % 12) + 1;
  const ultimoDiaDelMes = daysInMonth(anio, mes);
  const dia = diaCobro <= ultimoDiaDelMes ? diaCobro : ultimoDiaDelMes;
  return `${anio}-${pad2(mes)}-${pad2(dia)}`;
}

/**
 * Spec-fixed signature. The returned array holds `forwardPeriods +
 * backwardPeriods` contiguous half-open periods whose anchor is the
 * **containing** period of `baseDate` (design D1). Index `0` is
 * `backwardPeriods` periods before the anchor; the anchor sits at index
 * `backwardPeriods`.
 */
export function calculatePrepaidPeriods(
  baseDate: string,
  diaCobro: number,
  forwardPeriods: number,
  backwardPeriods = 0,
): ReissuePeriod[] {
  const { day, month, year } = parseDateOnly(baseDate);
  // Containing anchor: when the base day-of-month is strictly before the plain
  // clamped start day of the base month, the current period started last month.
  const plainStartDom = Math.min(diaCobro, daysInMonth(year, month));
  const anchorShift = day < plainStartDom ? -1 : 0;

  const total = forwardPeriods + backwardPeriods;
  const periods: ReissuePeriod[] = [];
  for (let index = 0; index < total; index += 1) {
    const offset = index - backwardPeriods + anchorShift;
    periods.push({
      inicio: operationalPeriodStart(diaCobro, baseDate, offset),
      fin: operationalPeriodStart(diaCobro, baseDate, offset + 1),
    });
  }
  return periods;
}

/**
 * The two most-recently-started operational periods for a client's billing
 * day at a business date: `{ current, previous }`, with `previous.fin ===
 * current.inicio`. This is the spec formula, verbatim.
 */
export function calculateReissueWindow(diaCobro: number, fechaBase: string): ReissueWindow {
  const current = calculatePrepaidPeriods(fechaBase, diaCobro, 1, 0)[0];
  const previous = calculatePrepaidPeriods(fechaBase, diaCobro, 1, 1)[0];
  if (current === undefined || previous === undefined) {
    throw new Error('the reissue window requires at least one period');
  }
  return { current, previous };
}

/** True when the invoice's start equals either period start in the window. */
export function isInReissueWindow(invoiceFechaInicio: string, window: ReissueWindow): boolean {
  return (
    window.current.inicio === invoiceFechaInicio ||
    window.previous.inicio === invoiceFechaInicio
  );
}

/**
 * Today's business-calendar date (`YYYY-MM-DD`) in the billing timezone.
 * Pure Intl date-only formatting; the result is independent of the host
 * process timezone because the requested `timeZone` is always explicit.
 *
 * The optional `now` argument exists for deterministic tests (inject a fixed
 * instant). The backend keeps its own `core/utils/fechas.businessDateInTimeZone`;
 * this shared helper is for the frontend display-only gate.
 */
export function businessDateInTimeZone(
  timeZone: string = 'America/Guatemala',
  now: Date = new Date(),
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}
